import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import { getAppStore, subscribeAppStore } from '@/store';
import { watchProjectCredits } from '@/features/credits';
import { isFreePreset } from '@/hooks/useFreePresets';
import { isMobile } from '@/consts/device';
import { Screen } from '@/types';
import { ScreenType } from '@/types/analytics';
import isAuthorized from '@/utils/user/isAuthorized';
import sendCreditsInfoAnalytics from '@/utils/analytics/sendCreditsInfoAnalytics';
import watchCreditsBalance from '@/utils/analytics/watchCreditsBalance';

import type { CreditsInfoTrigger } from '@/types/analytics';

interface TrackPresetCreditsParams {
  slug: string;
  category: string;
  trigger?: CreditsInfoTrigger;
}

interface WatchPresetShownParams {
  slug: string;
  onShown: (flowId: string) => void;
  onLeft: () => void;
}

/**
 * The screen a preset opens on — the feed on mobile, the generator otherwise
 * (`getScreenType`'s rule for `Screen.Create`). Fixed rather than read at send
 * time: a guest's event goes out before the navigation it belongs to.
 */
const PRESET_SCREEN_TYPE = isMobile
  ? ScreenType.PRESET_FEED
  : ScreenType.GENERATOR;

const getShownFlowId = (slug: string): string | null => {
  const { screen, selectedPreset } = getAppStore();
  if (screen !== Screen.Create || selectedPreset?.slug !== slug) return null;
  return selectedPreset.flowId || null;
};

/**
 * `onShown` once the Create screen shows `slug`; after that, `onLeft` the
 * moment it stops showing it (back to the library, another preset). Opens
 * write `screen` and `selectedPreset` in the same update, so there is no
 * in-between state to misread.
 */
const watchPresetShown = ({
  slug,
  onShown,
  onLeft,
}: WatchPresetShownParams): (() => void) => {
  let hasShown = false;
  const check = () => {
    const flowId = getShownFlowId(slug);
    if (flowId && !hasShown) {
      hasShown = true;
      onShown(flowId);
    } else if (!flowId && hasShown) {
      onLeft();
    }
  };

  const unsubscribe = subscribeAppStore(check);
  check();
  return unsubscribe;
};

let disposeActive: (() => void) | undefined;

/**
 * Sends `credits_info` for a preset the user just opened, once both numbers are
 * real: the price the price API returned for it and the balance next to it.
 *
 * Never requests anything — it waits on the quote the screen's own
 * `useProjectCredits` asks for, so a late quote only delays this event and
 * the open events stay untouched. Sends only while the preset is still on
 * screen: leaving it (back, or opening/swiping to another) drops the wait, so
 * a preset left before its price arrived sends nothing. Guests get `null`s at
 * once — the price API is authenticated, so there is nothing to wait for.
 */
const trackPresetCredits = ({
  slug,
  category,
  trigger = 'preset_open',
}: TrackPresetCreditsParams) => {
  disposeActive?.();
  const startedAt = Date.now();
  const disposers: Array<() => void> = [];
  let isActive = true;
  const dispose = () => {
    isActive = false;
    disposers.splice(0).forEach((unsubscribe) => unsubscribe());
  };
  disposeActive = dispose;
  // A watcher can deliver before it returns, finishing the run first — its
  // unsubscribe then runs at once instead of joining an emptied list.
  const track = (unsubscribe: () => void) => {
    if (isActive) disposers.push(unsubscribe);
    else unsubscribe();
  };

  const send = ({
    presetCredits,
    balance,
  }: {
    presetCredits: number | null;
    balance: number | null;
  }) => {
    dispose();
    sendCreditsInfoAnalytics({
      screenType: PRESET_SCREEN_TYPE,
      settings: {
        category,
        id: slug,
        trigger,
        preset_credits: presetCredits,
        credits_balance: balance,
      },
    });
  };

  if (!isAuthorized()) {
    send({ presetCredits: null, balance: null });
    return;
  }

  const sendWithBalance = (presetCredits: number | null) => {
    if (!isActive) return;
    track(watchCreditsBalance((balance) => send({ presetCredits, balance })));
  };

  isFreePreset(slug)
    .then((isFree) => {
      if (!isActive) return;
      track(
        watchPresetShown({
          slug,
          onShown: (flowId) => {
            if (!isActive) return;
            if (isFree) {
              sendWithBalance(0);
              return;
            }
            track(
              watchProjectCredits({
                flowId,
                since: startedAt,
                onResult: sendWithBalance,
              })
            );
          },
          onLeft: dispose,
        })
      );
    })
    .catch((error) =>
      errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN)
    );
};

export default trackPresetCredits;
